import { useState, useEffect } from 'react';
import { fetchHistory } from "../api/api";
import { useAuth } from '../utils/AuthContext';
import { useNavigate } from 'react-router-dom';
import StickyNavbar from "../components/StickyNavbar";
import Cookies from 'js-cookie';
import CSpinner from '../components/CSpinner';
import { formatDistanceToNow } from "date-fns";
import ToolCard from "../components/ToolCard";

const SearchHistoryList = () => {
  const [loading, setLoading] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const token = Cookies.get('authToken');

  const fetchData = async () => {
    try {
      if (token) {
        setLoading(true);
        const results = await fetchHistory(token);
        setHistoryList(results);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearchClick = (query) => {
    
    try {
      navigate("/", { 
        state: { 
          searchQuery: query 
        } 
      });
    } catch (error) {
      console.error('Error navigating to category tools:', error);
    }
  };

  const openToolsModal = (history) => {
    setSelectedHistory(history);
    setIsModalOpen(true);
    // Prevent body scrolling when modal is open
    document.body.style.overflow = 'hidden';
  };

  const closeToolsModal = () => {
    setIsModalOpen(false);
    // Re-enable body scrolling
    document.body.style.overflow = 'auto';
  };

  const filteredHistory = historyList.filter(item => 
    item.query.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const groupHistoryByDate = () => {
    const groups = {};
    
    filteredHistory.forEach(item => {
      const date = new Date(item.timestamp);
      const dateString = date.toLocaleDateString();
      
      if (!groups[dateString]) {
        groups[dateString] = [];
      }
      
      groups[dateString].push(item);
    });
    
    return groups;
  };

  const groupedHistory = groupHistoryByDate();

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <StickyNavbar />
      
      <div className="w-full max-w-4xl mx-auto px-4 pt-24 pb-10">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-xl font-bold text-gray-900">Search History</h1>
              <button 
                onClick={fetchData} 
                className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
            
            {/* Search Input */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg className="w-4 h-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                </svg>
              </div>
              <input 
                type="search" 
                className="block w-full p-3 pl-10 text-sm text-gray-900 border border-gray-200 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Filter your search queries..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center p-10">
              <CSpinner />
            </div>
          )}
          
          {/* Empty State */}
          {!loading && historyList.length === 0 && (
            <div className="flex flex-col items-center justify-center p-10 text-center">
              <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No search history found</h3>
              <p className="text-gray-500">Start searching to see your history here.</p>
            </div>
          )}
          
          {/* No Results State */}
          {!loading && historyList.length > 0 && filteredHistory.length === 0 && (
            <div className="flex flex-col items-center justify-center p-10 text-center">
              <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No matching results</h3>
              <p className="text-gray-500">Try a different search term.</p>
            </div>
          )}
          
          {/* History List */}
          {!loading && filteredHistory.length > 0 && (
            <div className="divide-y divide-gray-100">
              {Object.entries(groupedHistory).map(([date, items]) => (
                <div key={date} className="px-6 py-4">
                  <h2 className="text-sm font-medium text-gray-500 uppercase mb-3">{date}</h2>
                  <ul className="space-y-3">
                    {items.map((history, index) => (
                      <li key={index} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors duration-150">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0 bg-blue-50 rounded-lg p-2">
                            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                            </svg>
                          </div>
                          <div className="flex-grow">
                            <div 
                              className="text-base font-medium text-gray-900 mb-1 w-full truncate"
                            >
                              {history.query}
                            </div>
                            <div className="flex items-center space-x-3 text-sm">
                              <span className="text-gray-500">
                                {formatDistanceToNow(new Date(history.timestamp), { addSuffix: true })}
                              </span>
                              <button 
                                className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
                                onClick={() => openToolsModal(history)}
                              >
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"></path>
                                </svg>
                                View {history.tools.length} Tool{history.tools.length !== 1 ? 's' : ''}
                              </button>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button 
                              className="flex-shrink-0 text-gray-400 hover:text-blue-600 p-1 rounded-full hover:bg-blue-50"
                              onClick={() => openToolsModal(history)}
                              title="View results"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                              </svg>
                            </button>
                            
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tools Modal */}
      {isModalOpen && selectedHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-100">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Search Results</h2>
                <p className="text-sm text-gray-500 mt-1">
                  <span className="font-medium">"{selectedHistory.query}"</span> • 
                  {' ' + formatDistanceToNow(new Date(selectedHistory.timestamp), { addSuffix: true })}
                </p>
              </div>
              <button 
                onClick={closeToolsModal}
                className="text-gray-400 hover:text-gray-600 rounded-full p-2 hover:bg-gray-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="overflow-y-auto p-6 flex-grow">
              {selectedHistory.tools.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-10 text-center">
                  <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 mb-1">No tools found</h3>
                  <p className="text-gray-500">This search didn't return any tools.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {selectedHistory.tools.map((tool, index) => (
                    <ToolCard key={tool._id} tool={tool} />
                  ))}
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-100 flex justify-between">
              <button
                onClick={closeToolsModal}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              {/*(<button
                onClick={() => {
                  closeToolsModal();
                  handleSearchClick(selectedHistory.query);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                Search Again
              </button>)*/}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchHistoryList;